import * as React from "react";
import { Box, Flex, Spacer, Stack, Text, IconButton } from "@chakra-ui/react";
import { EditIcon } from "@chakra-ui/icons";
import { PersonalObjective } from "../../types";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { MainObjectiveModal } from "../MainObjectiveModal";
import { useSnackbar } from "notistack";
import { useStores } from "../../hooks/useStores";
import { observer } from "mobx-react";
import moment from "moment";
import ReactGA from "react-ga";
import ReactPixel from "react-facebook-pixel";

type Props = {
  userId: string;
  boxShadow?: string;
  mt?: string;
  mb?: string;
  isSubscriptionDeactivated?: boolean;
};

export const MainObjective: React.FC<Props> = observer(
  ({ userId, boxShadow, mt, mb, isSubscriptionDeactivated = false }) => {
    const { t } = useTranslation();
    const [isModalVisible, setIsModalVisible] = useState(false);

    const [personalObjective, setPersonalObjective] =
      useState<PersonalObjective | null>();
    const { enqueueSnackbar } = useSnackbar();
    const { clientsStore } = useStores();

    const getPersonalObjective = useCallback(async () => {
      try {
        await clientsStore.getPersonalObjective(userId);
        setPersonalObjective(clientsStore.personalObjective);
      } catch (error) {
        console.log("ERR: ", error);
      } finally {
      }
    }, [clientsStore, userId]);

    useEffect(() => {
      getPersonalObjective();
    }, [getPersonalObjective]);

    const handleSubmit = useCallback(
      async (data: any) => {
        try {
          //CREATE
          await clientsStore.addPersonalObjective(userId, data);

          ReactGA.event({
            category: "Customer",
            action: "Setup Obiettivi",
          });
          ReactPixel.track("Setup Obiettivi");

          setIsModalVisible(false);
          setPersonalObjective(clientsStore.personalObjective);
        } catch (err) {
          console.log(err);
          enqueueSnackbar(t("screens.client.emailForm.submitError"), {
            variant: "error",
          });
        }
      },
      [clientsStore, enqueueSnackbar, t, userId]
    );

    return (
      <>
        <Box mt={mt} mb={mb}>
          <Flex>
            <Box pl="4" alignSelf="center">
              <Text fontSize="20px">
                {t("screens.client.personalObjective.title")}
              </Text>
            </Box>
            <Spacer />
            <Box pr="4">
              <IconButton
                aria-label="edit"
                variant="ghost"
                colorScheme="black"
                fontSize="20px"
                icon={<EditIcon />}
                onClick={() => {
                  setIsModalVisible(true);
                }}
                disabled={isSubscriptionDeactivated}
              />
            </Box>
          </Flex>

          <Flex>
            <Box
              w={"100%"}
              p="4"
              border="1px"
              borderColor="#EBF3F8"
              borderRadius="16px"
              mt={"10px"}
              boxShadow={boxShadow}
            >
              <Stack spacing={3}>
                <Flex>
                  <Text>{t("screens.client.personalObjective.height")}</Text>
                  <Spacer />
                  <Text>
                    {personalObjective?.height
                      ? personalObjective?.height + " cm"
                      : "0 cm"}
                  </Text>
                </Flex>
                <Flex>
                  <Text>{t("screens.client.personalObjective.weight")}</Text>
                  <Spacer />
                  <Text>
                    {personalObjective?.weight
                      ? personalObjective?.weight + " kg"
                      : "0 kg"}
                  </Text>
                </Flex>
                <Flex>
                  <Text>{t("screens.client.personalObjective.lean_mass")}</Text>
                  <Spacer />
                  <Text>
                    {personalObjective?.lean_mass
                      ? personalObjective?.lean_mass + " %"
                      : "0 %"}
                  </Text>
                </Flex>
                <Flex>
                  <Text>{t("screens.client.personalObjectiveForm.data")}</Text>
                  <Spacer />
                  <Text>
                    {personalObjective
                      ? moment(personalObjective.data).format("DD-MM-YYYY")
                      : ""}
                  </Text>
                </Flex>
              </Stack>
            </Box>
          </Flex>
        </Box>
        <MainObjectiveModal
          isOpen={isModalVisible}
          personalObjective={personalObjective}
          onClose={() => {
            setIsModalVisible(false);
          }}
          onSubmit={handleSubmit}
        />
      </>
    );
  }
);
